"use client"

import type React from "react"
import { useState, useEffect, useMemo, useCallback, useRef } from "react"
import { CSVLink } from "react-csv"
import type { ColumnDef, SortingState } from "@tanstack/react-table"
import {
  Plus,
  Pencil,
  Trash2,
  FileDown,
  Users,
  CheckCircle2,
  XCircle,
  Upload,
  Key,
} from "lucide-react"
import { Table } from "./table/invoiceTable.tsx"
import { Modal } from "./customerModal.tsx"
import { MODAL_CANCEL_BTN, MODAL_FOOTER, MODAL_PRIMARY_BTN } from "./ui/modalStyles.ts"
import { Topbar } from "./topNavbar.tsx"
import { useOptionalAdminChrome } from "../context/AdminLayoutContext.tsx"
import { Sidebar } from "./sideNavbar.tsx"
import { getToken, getRole } from "../utils/auth.ts"
import { toast } from "../utils/notify.ts";
import axiosInstance from "../utils/axiosConfig.ts"
import { EnhancedBulkAddModal } from "./modals/EnhancedBulkAddModal.tsx"
import { CRUD_FILTER_CONFIGS, mergeQuickFilterOptions } from "../config/crudFilterConfigs.ts"
import { useCrudTableFilters } from "../hooks/useCrudTableFilters.ts"
import { useCrudPeriodFilter } from "../hooks/useCrudPeriodFilter.ts"
import { getCrudPeriodConfig } from "../config/crudPeriodConfigs.ts"
import { useCrudListSession } from "../hooks/useCrudListSession.ts"
import { CrudStatsSection } from "./crud/CrudStatsSection.tsx"
import { CrudListHeader } from "./crud/CrudListHeader.tsx"
import { CrudBulkSelectionBar } from "./crud/CrudBulkSelectionBar.tsx"
import { periodQueryParamsForTextSearch } from "../utils/crudPeriodUtils.ts"
import type { StatCardDef } from "../types/crudFilters.ts"
import { CustomerPortalCredentialsModal } from "./modals/CustomerPortalCredentialsModal.tsx"
import { normalizeCustomerEditForm } from "../utils/normalizeEditFormData.ts"

const LIST_SESSION_KEY = "customer-management-list"

interface CRUDPageProps<T> {
  title: string
  endpoint: string
  columns: ColumnDef<T>[]
  FormComponent: React.ComponentType<{
    formData: Partial<T>
    handleInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    handleFileRemove?: (fieldName: string) => void // Add this
    isEditing: boolean
    validateBeforeSubmit?: (formData: Partial<T>) => string | null
    supportsBulkAdd?: boolean
    validationErrors?: Record<string, string>
    loadingStates?: Record<string, boolean>
    setLoadingStates?: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  }>
  onDataChange?: () => void
  validateBeforeSubmit?: (formData: Partial<T>) => string | null
  supportsBulkAdd?: boolean
}
export function CRUDPage<T extends { id: string; is_active?: boolean }>({
  title,
  endpoint,
  columns,
  FormComponent,
  onDataChange,
  validateBeforeSubmit,
  supportsBulkAdd = false,
}: CRUDPageProps<T>) {
  const listSession = useCrudListSession<T>(LIST_SESSION_KEY)
  const {
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    search,
    setSearch,
    sorting,
    setSorting,
    persist,
    saveScroll,
    restoreScroll,
    hadInitialCache,
  } = listSession

  const [data, setData] = useState<T[]>(listSession.cachedData ?? [])
  const [totalCount, setTotalCount] = useState(listSession.cachedTotal ?? 0)
  const [stats, setStats] = useState<Record<string, number>>(
    listSession.cachedStats ?? { total: 0, active: 0, inactive: 0 },
  )
  const [filterOptions, setFilterOptions] = useState<{ areas: string[]; service_plans: string[]; isps: string[] }>({
    areas: [],
    service_plans: [],
    isps: [],
  })
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<T | null>(null)
  const [formData, setFormData] = useState<Partial<T>>({})
  const hasChrome = useOptionalAdminChrome()
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(!hadInitialCache)
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [isBulkAddModalVisible, setIsBulkAddModalVisible] = useState(false)
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})
  const [credentialsCustomerId, setCredentialsCustomerId] = useState<string | null>(null)
  const isCompanyOwner = getRole() === "company_owner"
  const filterConfig = CRUD_FILTER_CONFIGS.customer
  const periodConfig = getCrudPeriodConfig("customer")
  const tableFilters = useCrudTableFilters({
    config: filterConfig,
    onFilterChange: () => setPageIndex(0),
  })
  const periodFilter = useCrudPeriodFilter({
    config: periodConfig,
    onPeriodChange: () => setPageIndex(0),
  })
  const restoredFiltersRef = useRef(false)

  const buildSortQuery = (s: SortingState) => s.map((x) => `${x.id}:${x.desc ? "desc" : "asc"}`).join(",")

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      setPageIndex(0)
    },
    [setPageIndex, setSearch],
  )

  const quickFilters = useMemo(
    () =>
      mergeQuickFilterOptions(filterConfig, {
        area: filterOptions.areas.map((a) => ({ value: a, label: a })),
        service_plan: filterOptions.service_plans.map((p) => ({ value: p, label: p })),
        isp: filterOptions.isps.map((name) => ({ value: name, label: name })),
      }),
    [filterConfig, filterOptions],
  )

  const statCards: StatCardDef[] = useMemo(
    () =>
      filterConfig.statCards.map((card) => ({
        ...card,
        value: stats[card.id] ?? stats.total ?? 0,
      })),
    [filterConfig.statCards, stats],
  )

  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>({
    cnic_front_image: false,
    cnic_back_image: false,
    agreement_document: false,
  })

  const fetchFilterOptions = useCallback(async () => {
    try {
      const token = getToken()
      const res = await axiosInstance.get(`/${endpoint}/filter-options`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      setFilterOptions({
        areas: res.data?.areas ?? [],
        service_plans: res.data?.service_plans ?? [],
        isps: res.data?.isps ?? [],
      })
    } catch {
      // non-blocking
    }
  }, [endpoint])

  const fetchStats = useCallback(async () => {
    try {
      const token = getToken()
      const res = await axiosInstance.get(`/${endpoint}/summary`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          ...tableFilters.serverQueryParams,
          ...periodQueryParamsForTextSearch(periodFilter.period, search),
        },
      })
      setStats({
        total: res.data.total ?? 0,
        active: res.data.active ?? 0,
        inactive: res.data.inactive ?? 0,
      })
      persist({
        stats: {
          total: res.data.total ?? 0,
          active: res.data.active ?? 0,
          inactive: res.data.inactive ?? 0,
        },
      })
    } catch {
      // keep previous stats
    }
  }, [endpoint, tableFilters.serverQueryParams, periodFilter.period, search])

  const fetchData = useCallback(async () => {
    const showSpinner = data.length === 0
    if (showSpinner) setIsLoading(true)
    try {
      const token = getToken()
      const res = await axiosInstance.get(`/${endpoint}/page`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pageIndex + 1,
          page_size: pageSize,
          sort: buildSortQuery(sorting) || undefined,
          q: search || undefined,
          ...tableFilters.serverQueryParams,
          ...periodQueryParamsForTextSearch(periodFilter.period, search),
        },
      })
      const items = res.data.items ?? []
      const total = Number(res.data.total ?? items.length)
      setData(items)
      setTotalCount(total)
      persist({
        data: items,
        totalCount: total,
        pageIndex,
        pageSize,
        search,
        sorting,
        filterState: tableFilters.filterState,
        period: periodFilter.period,
        fetchedAt: Date.now(),
      })
      if (onDataChange) onDataChange()
    } catch (err: any) {
      try {
        const token = getToken()
        const response = await axiosInstance.get(`/${endpoint}/list`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        const all = response.data ?? []
        setTotalCount(all.length)
        const start = pageIndex * pageSize
        setData(all.slice(start, start + pageSize))
      } catch (error) {
        console.error(`Failed to fetch ${title}`, error)
        toast.error(`Failed to fetch ${title}`)
      }
    } finally {
      setIsLoading(false)
    }
  }, [
    data.length,
    endpoint,
    onDataChange,
    pageIndex,
    pageSize,
    periodFilter.period,
    persist,
    search,
    sorting,
    tableFilters.filterState,
    tableFilters.serverQueryParams,
    title,
  ])

  useEffect(() => {
    return () => {
      saveScroll()
      persist({
        pageIndex,
        pageSize,
        search,
        sorting,
        filterState: tableFilters.filterState,
        period: periodFilter.period,
      })
    }
  }, [pageIndex, pageSize, search, sorting, tableFilters.filterState, periodFilter.period, persist, saveScroll])

  useEffect(() => {
    fetchFilterOptions()
  }, [fetchFilterOptions])

  useEffect(() => {
    if (restoredFiltersRef.current || !hadInitialCache) return
    restoredFiltersRef.current = true
    try {
      const raw = sessionStorage.getItem(LIST_SESSION_KEY)
      if (!raw) return
      const cache = JSON.parse(raw)
      if (cache.filterState) {
        Object.entries(cache.filterState).forEach(([field, value]) => {
          tableFilters.setQuickFilter(field, value as any)
        })
      }
      if (cache.period === null) {
        periodFilter.setAll()
      } else if (cache.period) {
        periodFilter.setPeriod(cache.period)
      }
    } catch {
      // ignore corrupt cache
    }
  }, [hadInitialCache, periodFilter, tableFilters])

  useEffect(() => {
    fetchData()
    fetchStats()
  }, [pageIndex, pageSize, JSON.stringify(sorting), search, JSON.stringify(tableFilters.mergedColumnFilters), periodFilter.period])

  useEffect(() => {
    restoreScroll()
  }, [data, restoreScroll])

  useEffect(() => {
    const onBeforeUnload = () => saveScroll()
    window.addEventListener("pagehide", onBeforeUnload)
    return () => window.removeEventListener("pagehide", onBeforeUnload)
  }, [saveScroll])

  const handleBulkStatusChange = async (newStatus: boolean) => {
    if (selectedRows.length === 0) return

    try {
      setIsLoading(true)
      const token = getToken()
      await Promise.all(
        selectedRows.map((id) =>
          axiosInstance.put(
            `/${endpoint}/update/${id}`,
            { is_active: newStatus },
            { headers: { Authorization: `Bearer ${token}` } },
          ),
        ),
      )
      toast.success(
        `${selectedRows.length} ${title.toLowerCase()}${selectedRows.length > 1 ? "s" : ""} ${newStatus ? "activated" : "deactivated"} successfully`,
      )
      await fetchData()
      setSelectedRows([])
    } catch (error) {
      console.error(`Failed to update ${title} status`, error)
      toast.error(`Failed to update ${title} status`)
    } finally {
      setIsLoading(false)
    }
  }

  const showModal = async (item: T | null) => {
    saveScroll()
    setEditingItem(item)
    setValidationErrors({})
    setLoadingStates({
      cnic_front_image: false,
      cnic_back_image: false,
      agreement_document: false,
    })
    if (item?.id) {
      try {
        const token = getToken()
        const res = await axiosInstance.get(`/${endpoint}/${item.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        setFormData(normalizeCustomerEditForm({ ...item, ...res.data } as Partial<T>))
      } catch {
        setFormData(item || {})
      }
    } else {
      setFormData({})
    }
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
    setEditingItem(null)
    setFormData({})
    setValidationErrors({})
    // Reset loading states when closing modal
    setLoadingStates({
      cnic_front_image: false,
      cnic_back_image: false,
      agreement_document: false,
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setValidationErrors({})

    try {
      const token = getToken()
      const formDataToSend = new FormData()

      // Add all form data to FormData object
      Object.keys(formData).forEach((key) => {
        const value = formData[key]

        if (value == null || value === "") {
          return
        }
        if (key === "packages" && Array.isArray(value)) {
          formDataToSend.append("packages_json", JSON.stringify(value.map((row: any) => ({
            id: row.id || undefined,
            service_plan_id: row.service_plan_id,
            discount_amount: Number(row.discount_amount) || 0,
          }))))
          return
        }
        if (key === "technicians" && Array.isArray(value)) {
          formDataToSend.append("technicians_json", JSON.stringify(value.map((row: any) => ({
            technician_id: row.technician_id || row.id,
            commission_amount: Number(row.commission_amount) || 0,
          }))))
          return
        }
        if (["service_plan_ids", "technician_id", "connection_commission_amount", "discount_amount", "patch_cord_count", "patch_cord_ethernet_count"].includes(key)) {
          return
        }
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (item != null && item !== "") {
              formDataToSend.append(key, item)
            }
          })
        } else if (["cnic_front_image", "cnic_back_image", "agreement_document"].includes(key)) {
          formDataToSend.append(key, value)
        } else {
          formDataToSend.append(key, value)
        }
      })

      // Debug: Log what's being sent
      console.log('FormData contents:')
      for (let [key, value] of formDataToSend.entries()) {
        console.log(key, value)
      }

      if (editingItem) {
        await axiosInstance.put(`/${endpoint}/update/${editingItem.id}`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        toast.success(`${title} updated successfully`)
      } else {
        await axiosInstance.post(`/${endpoint}/add`, formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        })
        toast.success(`${title} added successfully`)
      }
      fetchData()
      handleCancel()
    } catch (error: any) {
      console.error("Operation failed", error)

      if (error.response?.data?.errors) {
        setValidationErrors(error.response.data.errors)
        toast.error(error.response.data.errors)
      } else if (error.response?.data) {
        toast.error(error.response.data)
      } else {
        toast.error("Operation failed. Please try again.")
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (window.confirm(`Are you sure you want to delete this ${title.toLowerCase()}?`)) {
      try {
        setIsLoading(true)
        const token = getToken()
        await axiosInstance.delete(`/${endpoint}/delete/${id}`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        toast.success(`${title} deleted successfully`)
        fetchData()
      } catch (error) {
        console.error("Delete operation failed", error)
        toast.error("Delete operation failed")
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      setIsLoading(true)
      const token = getToken()
      await axiosInstance.patch(
        `/${endpoint}/toggle-status/${id}`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      )
      toast.success(`${title} status updated successfully`)
      fetchData()
    } catch (error) {
      console.error("Toggle status failed", error)
      toast.error(`Failed to update ${title} status`)
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, files } = e.target

    if (value) {
      // This is a file path string from FileUploadField
      setFormData((prev) => ({ ...prev, [name]: value }))
    } else if (files && files.length > 0) {
      // This is a direct file input (fallback)
      setFormData((prev) => ({ ...prev, [name]: files[0] }))
    }
  }
  const handleFileRemove = useCallback((fieldName: string) => {
    // Update form data to remove the file reference
    setFormData((prev) => ({
      ...prev,
      [fieldName]: ""
    }))
  }, [])
  const toggleSidebar = () => {
    setIsSidebarOpen((prev) => !prev)
  }

  const memoizedColumns = useMemo(() => {
    return [
      ...columns,
      {
        header: "Status",
        accessorKey: "is_active",
        filterFn: (row, columnId, filterValue) => {
          if (!filterValue) return true
          return String(row.getValue(columnId)) === String(filterValue)
        },
        cell: (info: any) => (
          <div className="flex items-center">
            <button
              onClick={() => handleToggleStatus(info.row.original.id, info.getValue())}
              className={`px-2 py-0.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${info.getValue()
                  ? "bg-emerald-green/10 text-emerald-green hover:bg-emerald-green/20"
                  : "bg-coral-red/10 text-coral-red hover:bg-coral-red/20"
                }`}
            >
              {info.getValue() ? (
                <>
                  <CheckCircle2 className="h-3 w-3" /> Active
                </>
              ) : (
                <>
                  <XCircle className="h-3 w-3" /> Inactive
                </>
              )}
            </button>
          </div>
        ),
      },
      {
        header: "Actions",
        id: "actions",
        cell: (info: any) => (
          <div className="flex items-center gap-1">
            {isCompanyOwner && (
              <button
                onClick={() => setCredentialsCustomerId(info.row.original.id)}
                className="h-8 w-8 inline-flex items-center justify-center text-white bg-deep-ocean rounded-md hover:bg-deep-ocean/90 transition-colors"
                title="Portal Credentials"
              >
                <Key className="h-3.5 w-3.5" />
              </button>
            )}
            <button
              onClick={() => showModal(info.row.original)}
              className="h-8 w-8 inline-flex items-center justify-center text-white bg-electric-blue rounded-md hover:bg-btn-hover transition-colors"
              title="Edit"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button
              onClick={() => handleDelete(info.row.original.id)}
              className="h-8 w-8 inline-flex items-center justify-center text-white bg-coral-red rounded-md hover:bg-coral-red/80 transition-colors"
              title="Delete"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ),
      },
    ]
  }, [columns, isCompanyOwner])

  return (
    <div className={hasChrome ? "flex-1 min-w-0 w-full" : "flex h-screen bg-light-sky/50"}>
      {!hasChrome && (
        <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} setIsOpen={setIsSidebarOpen} />
      )}
      <div className={hasChrome ? "flex-1 min-w-0 w-full" : "flex-1 flex flex-col overflow-hidden"}>
        {!hasChrome && <Topbar toggleSidebar={toggleSidebar} />}
        <main
          className={
            hasChrome
              ? "px-3 py-3 pb-24 sm:px-4 md:pb-3"
              : `flex-1 overflow-x-hidden overflow-y-auto bg-light-sky/50 px-3 py-3 pb-24 sm:px-4 pt-16 transition-all duration-300 md:pb-3 ${isSidebarOpen ? "lg:ml-64" : "ml-0 lg:ml-20"}`
          }
        >

          <div className="container mx-auto">
            <CrudListHeader
              title={`${title} Management`}
              icon={Users}
              onAdd={() => showModal(null)}
              addLabel={`Add New ${title}`}
              actions={
                <>
                  {supportsBulkAdd && (
                    <button
                      onClick={() => setIsBulkAddModalVisible(true)}
                      className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-deep-ocean px-3 text-sm text-white hover:bg-deep-ocean/90"
                    >
                      <Upload className="h-4 w-4" /> Bulk Add
                    </button>
                  )}
                  <CSVLink
                    data={data}
                    filename={`${title.toLowerCase()}.csv`}
                    className="inline-flex h-9 items-center gap-1.5 rounded-lg bg-golden-amber px-3 text-sm text-white hover:bg-golden-amber/90"
                  >
                    <FileDown className="h-4 w-4" /> Export CSV
                  </CSVLink>
                </>
              }
            />

            <div className="mb-3 rounded-xl border border-slate-gray/10 bg-white p-3 shadow-sm sm:p-4">
              <CrudStatsSection
                cards={statCards}
                activeStatId={tableFilters.activeStatId}
                onStatClick={tableFilters.applyStatFilter}
                period={periodFilter.period}
                periodLabel={periodFilter.label}
                periodActive={periodFilter.isActive}
                onSetPeriod={periodFilter.setPeriod}
                onSetPeriodAll={periodFilter.setAll}
              />
            </div>

            <CrudBulkSelectionBar
              selectedCount={selectedRows.length}
              entityLabel={title.toLowerCase()}
              onClear={() => setSelectedRows([])}
            >
              <button
                onClick={() => handleBulkStatusChange(true)}
                disabled={selectedRows.length === 0 || isLoading}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-emerald-green px-3 text-sm font-medium text-white hover:bg-emerald-green/90 disabled:opacity-50"
              >
                <CheckCircle2 className="h-4 w-4" /> Activate
              </button>
              <button
                onClick={() => handleBulkStatusChange(false)}
                disabled={selectedRows.length === 0 || isLoading}
                className="inline-flex h-9 items-center gap-1.5 rounded-md bg-coral-red px-3 text-sm font-medium text-white hover:bg-coral-red/90 disabled:opacity-50"
              >
                <XCircle className="h-4 w-4" /> Deactivate
              </button>
            </CrudBulkSelectionBar>

            {/* Table Section */}
            <div className="mb-4">
              <Table
                data={data}
                columns={memoizedColumns}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                handleToggleStatus={handleToggleStatus}
                isLoading={isLoading}
                serverMode={true}
                totalCount={totalCount}
                pageIndex={pageIndex}
                pageSize={pageSize}
                onPageChange={setPageIndex}
                onPageSizeChange={setPageSize}
                sorting={sorting}
                onSortingChange={setSorting}
                globalSearch={search}
                onGlobalSearch={handleSearchChange}
                onColumnFiltersChangeExternal={tableFilters.handleColumnFiltersChange}
                quickFilters={quickFilters}
                filterState={tableFilters.filterState}
                onQuickFilterChange={tableFilters.setQuickFilter}
                onClearFilters={tableFilters.clearAllFilters}
                hasActiveFilters={tableFilters.hasAnyActiveFilters}
                inlineFilterFields={tableFilters.inlineFields}
              />
            </div>
          </div>
        </main>
      </div>

      {/* Modal */}
      <Modal
        isVisible={isModalVisible}
        onClose={handleCancel}
        title={editingItem ? `Edit ${title}` : `Add New ${title}`}
        isLoading={isLoading}
        size="xl"
      >
        <form onSubmit={handleSubmit}>
          <FormComponent
            formData={formData}
            handleInputChange={handleInputChange}
            handleFileChange={handleFileChange}
            handleFileRemove={handleFileRemove} // Add this prop
            isEditing={!!editingItem}
            validationErrors={validationErrors}
            loadingStates={loadingStates}
            setLoadingStates={setLoadingStates}
          />
          <div className={MODAL_FOOTER}>
            <button
              type="button"
              onClick={handleCancel}
              className={MODAL_CANCEL_BTN}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className={MODAL_PRIMARY_BTN}
            >
              {isLoading ? (
                <>
                  <svg
                    className="animate-spin h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Processing...
                </>
              ) : editingItem ? (
                <>
                  <Pencil className="h-5 w-5" /> Update {title}
                </>
              ) : (
                <>
                  <Plus className="h-5 w-5" /> Create {title}
                </>
              )}
            </button>
          </div>
        </form>
      </Modal>
      <EnhancedBulkAddModal
        isVisible={isBulkAddModalVisible}
        onClose={() => setIsBulkAddModalVisible(false)}
        endpoint={endpoint}
        entityName={title}
        onSuccess={fetchData}
      />
      {credentialsCustomerId && (
        <CustomerPortalCredentialsModal
          customerId={credentialsCustomerId}
          onClose={() => setCredentialsCustomerId(null)}
        />
      )}
    </div>
  )
}
